// In-memory access-token store + refresh + React reactivity for the admin portal.
//
// DESIGN (firm decisions):
//  - Access token: EdDSA (Ed25519), ~15-min TTL, role claim "admin".
//    Kept ONLY in memory (this module) — never localStorage/sessionStorage —
//    to limit XSS blast radius.
//  - Refresh token: opaque random, stored server-side HASHED, rotated on every
//    use with reuse-detection. The browser holds it in an HttpOnly, Secure,
//    SameSite cookie set by the ADMIN backend — JS never reads it. So
//    tryRefresh() just calls the refresh endpoint with credentials included.
//
// IMPORTANT: this module must NOT import src/api/client.ts — client.ts imports
// getAccessToken/tryRefresh from here, so a back-import would create a cycle.
// tryRefresh() therefore uses raw fetch.

import { useSyncExternalStore } from "react";

import { API_BASE } from "./env";

let accessToken: string | null = null;
const listeners = new Set<() => void>();
function notify() {
  listeners.forEach((l) => l());
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  notify();
}

export function clearAccessToken(): void {
  accessToken = null;
  notify();
}

export function isAuthenticated(): boolean {
  return accessToken !== null;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useAuth() {
  const token = useSyncExternalStore(subscribe, getAccessToken, getAccessToken);
  return { accessToken: token, isAuthenticated: token !== null };
}

// Attempts to mint a fresh admin access token using the HttpOnly refresh cookie.
// Returns true on success. Rotation + reuse-detection are enforced
// server-side; the caller just retries the original request once.
//
// SINGLE-FLIGHT: the backend rotates the refresh token on every use with
// reuse-detection. If two concurrent 401s each fired their own refresh, the
// second would present an already-consumed token and the backend would revoke
// the entire token family (logging the admin out). So concurrent callers share
// ONE in-flight promise.
let inflight: Promise<boolean> | null = null;

export function tryRefresh(): Promise<boolean> {
  if (inflight) return inflight;
  inflight = doRefresh().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/admin/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      clearAccessToken();
      return false;
    }
    const data = (await res.json()) as { access_token?: string };
    if (!data.access_token) {
      clearAccessToken();
      return false;
    }
    setAccessToken(data.access_token);
    return true;
  } catch {
    clearAccessToken();
    return false;
  }
}
